/**
 * Reproduktory V2 — moduł zarządzania reproduktorami (UI 2.0).
 * AKTUALNE. Zastępuje legacy ProfilePanel?tab=studs.
 *
 * Sekcja „Reproduktory" = WSZYSTKIE psy do krycia / promowania, niezależnie od tego,
 * czy są dodane jako:
 *   - standalone stud (StudWizard → /studs, hodowla OPCJONALNA), albo
 *   - samiec dodany jako pies w hodowli (/kennels/{id}/dogs, gender=male).
 *
 * Lista łączy oba źródła (dedup po id). Edycja kieruje do właściwego kreatora
 * w zależności od źródła rekordu. „Dodaj reproduktora" → standalone StudWizard
 * (bez wymogu posiadania hodowli).
 */
import { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Award } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { ModuleListV2, ModuleCardV2, ConfirmDeleteDialog, useModuleDelete } from './ModuleListV2';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ReproduktoryV2() {
  const navigate = useNavigate();
  const { token } = useAuth();
  const headers = { Authorization: `Bearer ${token}` };

  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState([]);
  const redirectedRef = useRef(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      // 1) Standalone studs (StudWizard) — hodowla opcjonalna.
      let studs = [];
      try {
        const sRes = await axios.get(`${API}/studs`, { headers });
        studs = (sRes.data || []).map((d) => ({ ...d, _source: 'stud' }));
      } catch (e) { studs = []; }
      const studIds = new Set(studs.map((d) => d.id));

      // 2) Samce dodane jako psy hodowli — łączymy, żeby nic nie zniknęło użytkownikowi.
      let kennelMales = [];
      try {
        const kRes = await axios.get(`${API}/kennels`, { headers });
        const k = (kRes.data || [])[0];
        if (k) {
          const dRes = await axios.get(`${API}/kennels/${k.id}/dogs`, { headers });
          kennelMales = (dRes.data || [])
            .filter((d) => d.gender === 'male' && !studIds.has(d.id))
            .map((d) => ({ ...d, _source: 'kennelDog' }));
        }
      } catch (e) { kennelMales = []; }

      setItems([...studs, ...kennelMales]);
    } catch (e) {
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { load(); }, [load]);

  // Brak reproduktorów → od razu kreator (pomijamy pusty ekran „Dodaj reproduktora").
  useEffect(() => {
    if (loading || redirectedRef.current) return;
    if (items.length === 0) {
      redirectedRef.current = true;
      navigate('/wizard/reproduktor', { replace: true });
    }
  }, [loading, items, navigate]);

  const del = useModuleDelete(
    async (dog) => {
      if (dog.listing_type === 'stud' || dog._source === 'stud') {
        await axios.delete(`${API}/studs/${dog.id}`, { headers });
      } else {
        await axios.delete(`${API}/dogs/${dog.id}`, { headers });
      }
      toast.success('Reproduktor usunięty');
    },
    load
  );

  // „Dodaj reproduktora" → standalone StudWizard (osobna strona psa, hodowla opcjonalna).
  const handleAdd = () => navigate('/wizard/reproduktor');

  const editPath = (d) =>
    d.listing_type === 'stud' || d._source === 'stud'
      ? `/wizard/reproduktor/${d.id}`
      : `/panel/pies/${d.id}/edytuj`;

  return (
    <>
      <ModuleListV2
        activeKey="studs"
        contextLabel="Reproduktory"
        icon={Award}
        title="Reproduktory"
        subtitle="Psy do krycia i promowania — z hodowli lub jako samodzielne strony"
        addLabel="Dodaj reproduktora"
        onAdd={handleAdd}
        loading={loading || items.length === 0}
        items={items}
        empty={{
          icon: Award,
          title: 'Nie masz jeszcze dodanego reproduktora',
          text: 'Utwórz osobną stronę psa z opisem, zdjęciami, badaniami, rodowodem i formularzem zapytań.',
        }}
      >
        {items.map((d) => (
          <ModuleCardV2
            key={d.id}
            id={d.id}
            image={d.main_photo}
            title={d.name}
            subtitle={d.breed}
            badges={[
              { label: 'Reproduktor', className: 'bg-blue-50 text-blue-600' },
              ...(d.stud_available ? [{ label: 'Dostępny do krycia', className: 'bg-emerald-50 text-emerald-700' }] : []),
              ...(d.titles ? [{ label: d.titles, className: 'bg-amber-50 text-amber-700' }] : []),
            ]}
            onEdit={() => navigate(editPath(d))}
            onPreview={d.slug ? () => window.open(`/reproduktor/${d.slug}`, '_blank') : () => navigate(editPath(d))}
            onDelete={() => del.setTarget(d)}
          />
        ))}
      </ModuleListV2>
      <ConfirmDeleteDialog {...del.dialogProps} />
    </>
  );
}
